import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

const AboutSection = styled.section`
  padding: 80px 0;
  background: #fff;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const AboutContent = styled.div``;

const SectionTag = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;

  .icon {
    width: 8px;
    height: 8px;
    background: var(--secondary-color);
    border-radius: 50%;
  }

  span {
    color: var(--secondary-color);
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.9rem;
  }
`;

const AboutTitle = styled.h2`
  font-family: "Inter", sans-serif;
  font-size: 3.2rem;
  color: var(--text-dark);
  margin-bottom: 2rem;
  line-height: 1.2;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 2.4rem;
  }
`;

const AboutDescription = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 1.2rem;
  color: var(--text-light);
  line-height: 1.8;
  margin-bottom: 2.5rem;

  strong {
    color: var(--text-dark);
    font-weight: 600;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;

  li {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 15px;
    font-size: 1rem;
    color: var(--text-light);

    .icon {
      width: 8px;
      height: 8px;
      background: var(--secondary-color);
      border-radius: 50%;
      flex-shrink: 0;
    }
  }
`;

const ContactCard = styled.div`
  background: linear-gradient(
    135deg,
    var(--background-light) 0%,
    var(--background-medium) 100%
  );
  border-radius: 15px;
  padding: 30px;
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 40px;

  .icon {
    width: 60px;
    height: 60px;
    background: var(--secondary-color);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.5rem;
  }

  .content {
    h3 {
      margin: 0 0 8px 0;
      color: var(--text-dark);
      font-size: 1.25rem;
    }

    a {
      color: var(--secondary-color);
      text-decoration: none;
      font-weight: 600;
      font-size: 1.1rem;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const AboutVisual = styled.div`
  position: relative;
`;

const MainImage = styled.div`
  background: linear-gradient(
    135deg,
    var(--background-light) 0%,
    var(--background-medium) 100%
  );
  border-radius: 20px;
  padding: 40px;
  text-align: center;
  margin-bottom: 30px;

  img {
    max-width: 100%;
    height: auto;
    border-radius: 15px;
  }
`;

const StatsCard = styled.div`
  background: var(--background-dark);
  color: white;
  border-radius: 15px;
  padding: 30px;
  text-align: center;
  position: relative;

  .stats-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;

    @media (max-width: 480px) {
      grid-template-columns: 1fr;
      gap: 20px;
    }
  }

  .stat-item {
    .number {
      font-size: 2.5rem;
      font-weight: 700;
      color: var(--secondary-color);
      font-family: "Phudu", sans-serif;
      display: block;
      margin-bottom: 8px;
    }

    .label {
      color: #b8c5d1;
      font-size: 0.9rem;
    }
  }
`;

const AboutUs = () => {
  return (
    <AboutSection>
      <Container>
        <AboutGrid>
          <AboutContent>
            <SectionTag>
              <div className="icon"></div>
              <span>About Us</span>
            </SectionTag>

            <AboutTitle>Seamless Container Purchase and Delivery</AboutTitle>

            <AboutDescription>
              At <strong>Baypoint Storage</strong>, we believe containers are
              more than steel boxes—they're <strong>versatile solutions</strong>{" "}
              for storage, business, and living. From homeowners needing extra
              space to businesses looking for reliable storage or custom builds,
              our mission is to deliver containers that are durable, affordable,
              and tailored to your needs.
            </AboutDescription>

            <FeatureGrid>
              <FeatureList>
                <li>
                  <div className="icon"></div>
                  Trust & Reliability
                </li>
                <li>
                  <div className="icon"></div>
                  Supply Chain Solutions
                </li>
                <li>
                  <div className="icon"></div>
                  End-to-End Transportation
                </li>
              </FeatureList>

              <FeatureList>
                <li>
                  <div className="icon"></div>
                  50 US States Covered
                </li>
                <li>
                  <div className="icon"></div>
                  Contract Logistics
                </li>
                <li>
                  <div className="icon"></div>
                  Cargo Storage
                </li>
              </FeatureList>
            </FeatureGrid>

            <ContactCard>
              <div className="icon">
                <i className="fas fa-phone"></i>
              </div>
              <div className="content">
                <h3>Have Questions?</h3>
                <a href="tel:+14845429847">+1 (484) 542-9847</a>
              </div>
            </ContactCard>

            <Link to="/about" className="btn btn-secondary">
              Read More
              <i className="fas fa-chevron-right"></i>
            </Link>
          </AboutContent>

          <AboutVisual>
            <MainImage>
              <img
                src="/assets/2025/08/Untitled-design-4-688x1024.jpg"
                alt="Container facility"
              />
            </MainImage>

            <StatsCard>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="number">2K+</span>
                  <span className="label">Happy Clients</span>
                </div>
                <div className="stat-item">
                  <span className="number">50</span>
                  <span className="label">US States</span>
                </div>
              </div>
            </StatsCard>
          </AboutVisual>
        </AboutGrid>
      </Container>
    </AboutSection>
  );
};

export default AboutUs;
